import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, message, Upload,
  Image, Collapse, Tag, Typography, Popconfirm, Progress, Space,
  Skeleton, Empty,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, InboxOutlined,
  PictureOutlined, CloseOutlined, MedicineBoxOutlined,
} from '@ant-design/icons';
import {
  getCropDiseasesApi, addCropDiseaseApi, updateCropDiseaseApi,
  deleteImageApi, deleteDiseaseApi,
} from '../../api/cropDiseases';
import { getCropNamesApi } from '../../api/crops';
import { IMAGE_BASE } from '../../utils/constants';

const { Title, Text } = Typography;

const DISEASE_IMAGE = (f) => `${IMAGE_BASE}/disease_images/${f}`;

// ─── ImageGallery ──────────────────────────────────────────────────────────

function ImageGallery({ images, setImages, diseaseId }) {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (filename) => {
    const prev = [...images];
    setImages((imgs) => imgs.filter((i) => i !== filename));
    setDeleting(filename);
    try {
      await deleteImageApi({ id: diseaseId, image: filename });
      message.success('Image removed');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to remove image');
      setImages(prev);
    } finally {
      setDeleting(null);
    }
  };

  if (!images || images.length === 0) {
    return <Text type="secondary" style={{ fontSize: 13 }}>No existing images.</Text>;
  }

  return (
    <Image.PreviewGroup>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {images.map((img) => (
          <div key={img} style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
            <Image
              src={DISEASE_IMAGE(img)}
              width={80}
              height={80}
              style={{ objectFit: 'cover', borderRadius: 6 }}
            />
            <button
              onClick={() => handleDelete(img)}
              disabled={deleting === img}
              style={{
                position: 'absolute', top: -6, right: -6,
                width: 22, height: 22, border: 'none',
                background: deleting === img ? '#ff7875' : '#ff4d4f',
                color: '#fff', borderRadius: '50%',
                cursor: deleting === img ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, fontSize: 10, fontWeight: 700, lineHeight: 1,
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }}
            >
              {deleting === img ? '…' : '×'}
            </button>
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
}

// ─── ImageUploader ─────────────────────────────────────────────────────────

function ImageUploader({ fileList, onChange }) {
  return (
    <Upload.Dragger
      multiple
      beforeUpload={() => false}
      fileList={fileList}
      onChange={({ fileList: fl }) => onChange(fl)}
      accept="image/*"
      listType="picture"
      style={{ borderRadius: 8 }}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined style={{ color: '#1a6b2a', fontSize: 32 }} />
      </p>
      <p style={{ fontSize: 13, margin: '4px 0' }}>
        Drag images here or{' '}
        <span style={{ color: '#1a6b2a', fontWeight: 600 }}>click to select</span>
      </p>
      <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>JPG, PNG, WEBP...</p>
    </Upload.Dragger>
  );
}

// ─── DiseaseForm ───────────────────────────────────────────────────────────

function DiseaseForm({ mode, disease, cropNames, onClose, onRefresh }) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [existingImages, setExistingImages] = useState(disease?.images || []);
  const [cropSearch, setCropSearch] = useState('');
  const [cropOptions, setCropOptions] = useState(
    cropNames.map((c) => ({ value: c.common_id, label: c.name }))
  );
  const [loadingCrops, setLoadingCrops] = useState(false);

  useEffect(() => {
    if (disease) {
      form.setFieldsValue({
        common_id: disease.common_id,
        disease_name: disease.disease_name,
      });
    }
  }, [disease, form]);

  const handleCropSearch = async (val) => {
    setCropSearch(val);
    if (!val) return;
    setLoadingCrops(true);
    try {
      const res = await getCropNamesApi({ lang: 'en', crop: val });
      setCropOptions((res.data || []).map((c) => ({ value: c.common_id, label: c.name })));
    } catch {
      // keep existing options
    } finally {
      setLoadingCrops(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    const values = form.getFieldsValue();

    if (mode === 'add' && fileList.length === 0) {
      message.error('Please add at least one image');
      return;
    }

    setSubmitting(true);
    setProgress(0);
    try {
      const fd = new FormData();
      if (mode === 'add') {
        fd.append('common_id', values.common_id);
        fd.append('disease_name', values.disease_name);
        fileList.forEach((f) => fd.append('images[]', f.originFileObj));
        await addCropDiseaseApi(fd, (e) => {
          setProgress(Math.round((e.loaded / e.total) * 100));
        });
        message.success('Disease added');
      } else {
        fd.append('id', disease.id);
        fd.append('common_id', values.common_id);
        fd.append('disease_name', values.disease_name);
        if (fileList.length > 0) {
          fileList.forEach((f) => fd.append('images[]', f.originFileObj));
        }
        await updateCropDiseaseApi(fd, (e) => {
          setProgress(Math.round((e.loaded / e.total) * 100));
        });
        message.success('Disease updated');
      }
      onRefresh();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  };

  return (
    <div>
      <Form form={form} layout="vertical">
        <Form.Item
          label="Crop"
          name="common_id"
          rules={[{ required: true, message: 'Select a crop' }]}
        >
          <Select
            showSearch
            placeholder="Search crop..."
            options={cropOptions}
            loading={loadingCrops}
            onSearch={handleCropSearch}
            filterOption={false}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="Disease Name"
          name="disease_name"
          rules={[{ required: true, message: 'Enter disease name' }]}
        >
          <Input placeholder="e.g. Early Blight, Leaf Curl..." size="large" />
        </Form.Item>
      </Form>

      {mode === 'edit' && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            Existing Images
          </Text>
          <ImageGallery
            images={existingImages}
            setImages={setExistingImages}
            diseaseId={disease.id}
          />
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
          {mode === 'add' ? 'Images (required, min 1)' : 'Add More Images'}
        </Text>
        <ImageUploader fileList={fileList} onChange={setFileList} />
      </div>

      {submitting && progress > 0 && (
        <Progress percent={progress} size="small" style={{ marginBottom: 8 }} />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 8 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="primary" loading={submitting} onClick={handleSubmit}>
          {mode === 'add' ? 'Add Disease' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

// ─── DiseaseCard ───────────────────────────────────────────────────────────

function DiseaseCard({ disease, onEdit, onDelete }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDiseaseApi({ id: disease.id });
      message.success('Disease deleted');
      onDelete(disease.id);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to delete disease');
    } finally {
      setDeleting(false);
    }
  };

  const thumb = disease.images?.length > 0 ? DISEASE_IMAGE(disease.images[0]) : null;
  const extraCount = Math.max(0, (disease.images?.length || 0) - 1);

  return (
    <Card
      size="small"
      style={{ marginBottom: 10, borderRadius: 10, cursor: 'pointer' }}
      styles={{ body: { padding: '10px 14px' } }}
      onClick={() => onEdit(disease)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {thumb ? (
            <>
              <img
                src={thumb}
                alt={disease.disease_name}
                style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', display: 'block' }}
              />
              {extraCount > 0 && (
                <div style={{
                  position: 'absolute', bottom: 0, right: 0,
                  background: 'rgba(0,0,0,0.55)', color: '#fff',
                  fontSize: 9, fontWeight: 700, borderRadius: '0 0 8px 0',
                  padding: '1px 5px',
                }}>
                  +{extraCount}
                </div>
              )}
            </>
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 8,
              background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PictureOutlined style={{ color: '#d1d5db', fontSize: 20 }} />
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 2 }}>
            {disease.disease_name}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {disease.images?.length || 0} image{disease.images?.length !== 1 ? 's' : ''}
          </div>
        </div>

        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => { e.stopPropagation(); onEdit(disease); }}
          />
          <Popconfirm
            title="Delete this disease?"
            description="All associated images will also be removed."
            onConfirm={handleDelete}
            onCancel={(e) => e?.stopPropagation?.()}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deleting}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      </div>
    </Card>
  );
}

// ─── CropSection ───────────────────────────────────────────────────────────

function CropSection({ group, onEdit, onDeleteDisease }) {
  if (group.diseases.length === 0) {
    return (
      <Empty
        description="No diseases recorded for this crop yet"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: '16px 0' }}
      />
    );
  }
  return (
    <div>
      {group.diseases.map((d) => (
        <DiseaseCard key={d.id} disease={d} onEdit={onEdit} onDelete={onDeleteDisease} />
      ))}
    </div>
  );
}

// ─── CropDiseaseList ───────────────────────────────────────────────────────

function CropDiseaseList({ groups, loading, activeKeys, onActiveKeysChange, onEdit, onDeleteDisease }) {
  if (loading) {
    return (
      <div>
        {[1, 2, 3].map((i) => (
          <Card key={i} style={{ marginBottom: 16, borderRadius: 12 }}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </Card>
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Empty
        description="No crop diseases found. Add the first one!"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: 60 }}
      />
    );
  }

  const collapseItems = groups.map((group) => ({
    key: String(group.common_id),
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MedicineBoxOutlined style={{ color: '#1a6b2a' }} />
        <Text strong style={{ fontSize: 15 }}>{group.crop_name}</Text>
        <Tag color="green" style={{ marginLeft: 'auto', marginRight: 0 }}>
          {group.diseases.length} disease{group.diseases.length !== 1 ? 's' : ''}
        </Tag>
      </div>
    ),
    children: (
      <CropSection group={group} onEdit={onEdit} onDeleteDisease={onDeleteDisease} />
    ),
    style: {
      marginBottom: 12,
      borderRadius: 12,
      border: '1px solid #e8e8e8',
      overflow: 'hidden',
    },
  }));

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={onActiveKeysChange}
      items={collapseItems}
      style={{ background: 'transparent', border: 'none' }}
    />
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function AdminCropDiseases() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cropNames, setCropNames] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [selectedDisease, setSelectedDisease] = useState(null);

  const fetchDiseases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCropDiseasesApi();
      const data = res.data.data || [];
      setGroups(data);
      setActiveKeys(data.map((g) => String(g.common_id)));
    } catch {
      message.error('Failed to load crop diseases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiseases();
    getCropNamesApi({ lang: 'en', crop: '' })
      .then((res) => setCropNames(res.data || []))
      .catch(() => {});
  }, [fetchDiseases]);

  const openAdd = () => {
    setSelectedDisease(null);
    setModalMode('add');
  };

  const openEdit = (disease) => {
    setSelectedDisease(disease);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedDisease(null);
  };

  const handleDeleteDisease = (id) => {
    setGroups((prev) =>
      prev
        .map((g) => ({ ...g, diseases: g.diseases.filter((d) => d.id !== id) }))
        .filter((g) => g.diseases.length > 0)
    );
  };

  const totalDiseases = groups.reduce((acc, g) => acc + g.diseases.length, 0);

  return (
    <div>
      <div style={{
        marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Crop Disease Management</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {totalDiseases} disease{totalDiseases !== 1 ? 's' : ''} across {groups.length} crop{groups.length !== 1 ? 's' : ''}
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} size="large">
          Add Disease
        </Button>
      </div>

      <CropDiseaseList
        groups={groups}
        loading={loading}
        activeKeys={activeKeys}
        onActiveKeysChange={setActiveKeys}
        onEdit={openEdit}
        onDeleteDisease={handleDeleteDisease}
      />

      <Modal
        open={modalMode !== null}
        onCancel={closeModal}
        footer={null}
        title={modalMode === 'add' ? 'Add Crop Disease' : 'Edit Disease'}
        width={520}
        destroyOnClose
      >
        {modalMode && (
          <DiseaseForm
            mode={modalMode}
            disease={selectedDisease}
            cropNames={cropNames}
            onClose={closeModal}
            onRefresh={fetchDiseases}
          />
        )}
      </Modal>
    </div>
  );
}
